import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, Upload, Search, Download, Trash2, Edit, 
  AlertTriangle, CheckCircle, Clock, Plus, Filter,
  Calendar, Store, Package, Shield
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import api from '../lib/api';
import { useTutorial } from '../components/OnboardingTutorial';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

export default function InvoicesPage() {
  const { t, language } = useLanguage();
  const { shouldShow: showTutorial } = useTutorial();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState({ limit: 10, used: 0, remaining: 10 });
  const [expiringWarranties, setExpiringWarranties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [warrantyFilter, setWarrantyFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [tutorialActive, setTutorialActive] = useState(false);
  
  // Modal states
  const [showUpload, setShowUpload] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    file: null,
    product_name: '',
    store_name: '',
    purchase_date: '',
    amount: '',
    warranty_months: '',
    category: '',
    notes: ''
  });

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  const fetchInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (warrantyFilter) params.append('warranty_status', warrantyFilter);
      
      const response = await api.get(`/invoices/list?${params.toString()}`);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [searchQuery, categoryFilter, warrantyFilter]);

  const fetchLimits = async () => {
    try {
      const response = await api.get('/invoices/limits');
      setLimits(response.data);
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  const fetchExpiringWarranties = async () => {
    try {
      const response = await api.get('/invoices/alerts/expiring?days=30');
      setExpiringWarranties(response.data.expiring_invoices || []);
    } catch (error) {
      console.error('Error fetching expiring warranties:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/invoices/categories/list');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchInvoices(),
        fetchLimits(),
        fetchExpiringWarranties(),
        fetchCategories()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchInvoices]);

  const [analyzing, setAnalyzing] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(t('file_type_not_allowed'));
        return;
      }
      // Check file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('max_file_size'));
        return;
      }
      setFormData({ ...formData, file });
      
      // Auto-analyze if it's an image
      if (file.type.startsWith('image/')) {
        await analyzeWithOCR(file);
      }
    }
  };

  const analyzeWithOCR = async (file) => {
    setAnalyzing(true);
    try {
      const data = new FormData();
      data.append('file', file);
      
      const response = await api.post('/invoices/ocr/analyze', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success && response.data.data) {
        const extracted = response.data.data;
        setFormData(prev => ({
          ...prev,
          product_name: extracted.product_name || prev.product_name,
          store_name: extracted.store_name || prev.store_name,
          purchase_date: extracted.purchase_date || prev.purchase_date,
          amount: extracted.amount?.toString() || prev.amount,
          category: extracted.category || prev.category,
          warranty_months: extracted.warranty_months?.toString() || prev.warranty_months
        }));
        toast.success(t('info_extracted_auto'));
      } else {
        toast.info(t('fill_info_manually'));
      }
    } catch (error) {
      console.error('OCR error:', error);
      // Silent fail - user can still fill manually
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpload = async () => {
    if (!formData.file || !formData.product_name || !formData.store_name || !formData.purchase_date || !formData.amount) {
      toast.error(t('fill_required_fields'));
      return;
    }

    if (limits.remaining <= 0) {
      toast.error(t('invoice_limit_reached'));
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('product_name', formData.product_name);
      data.append('store_name', formData.store_name);
      data.append('purchase_date', formData.purchase_date);
      data.append('amount', formData.amount);
      if (formData.warranty_months) data.append('warranty_months', formData.warranty_months);
      if (formData.category) data.append('category', formData.category);
      if (formData.notes) data.append('notes', formData.notes);

      await api.post('/invoices/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('invoice_uploaded'));
      setShowUpload(false);
      setFormData({
        file: null,
        product_name: '',
        store_name: '',
        purchase_date: '',
        amount: '',
        warranty_months: '',
        category: '',
        notes: ''
      });
      fetchInvoices();
      fetchLimits();
      fetchExpiringWarranties();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.invoice_id}/download`);
      const { file_name, file_type, file_data } = response.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm(t('delete_invoice_confirm'))) {
      return;
    }
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success(t('invoice_deleted'));
      fetchInvoices();
      fetchLimits();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getWarrantyStatusBadge = (status) => {
    if (!status) return null;
    
    const badges = {
      active: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: t('warranty_active') },
      expiring_soon: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: t('warranty_expiring') },
      expired: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: t('warranty_expired') }
    };
    
    const badge = badges[status];
    if (!badge) return null;
    
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-BR' : 'en-US');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="w-6 h-6 text-primary" />
              {t('my_invoices')}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t('manage_invoices_desc')}
            </p>
          </div>
          <Button 
            onClick={() => setShowUpload(true)} 
            disabled={limits.remaining <= 0}
            data-testid="upload-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('upload_invoice')}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-primary">{limits.used}</p>
              <p className="text-xs text-muted-foreground">{t('storage_used')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{limits.limit}</p>
              <p className="text-xs text-muted-foreground">{t('of_limit')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {invoices.filter(i => i.warranty_status === 'active').length}
              </p>
              <p className="text-xs text-muted-foreground">{t('warranty_active')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{expiringWarranties.length}</p>
              <p className="text-xs text-muted-foreground">{t('warranty_expiring')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Expiring Warranties Alert */}
        {expiringWarranties.length > 0 && (
          <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="w-4 h-4" />
                {t('expiring_warranties')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {expiringWarranties.slice(0, 3).map(invoice => (
                  <div key={invoice.invoice_id} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{invoice.product_name}</span>
                    <span className="text-yellow-600">{invoice.days_remaining} {t('days_remaining')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search_invoices')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-invoices"
            />
          </div>
          <select
            value={warrantyFilter}
            onChange={(e) => setWarrantyFilter(e.target.value)}
            className="px-3 py-2 rounded-md border bg-background text-sm"
          >
            <option value="">{t('warranty_status')}</option>
            <option value="active">{t('warranty_active')}</option>
            <option value="expiring_soon">{t('warranty_expiring')}</option>
            <option value="expired">{t('warranty_expired')}</option>
          </select>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : invoices.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">{t('no_invoices_yet')}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowUpload(true)}
              >
                {t('add_first_invoice')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {invoices.map(invoice => (
              <Card key={invoice.invoice_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{invoice.product_name}</h3>
                        {getWarrantyStatusBadge(invoice.warranty_status)}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Store className="w-3 h-3" />
                          {invoice.store_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(invoice.purchase_date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="font-medium text-primary">{formatCurrency(invoice.amount)}</span>
                        {invoice.warranty_expires && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Shield className="w-3 h-3" />
                            {t('warranty_expires')}: {formatDate(invoice.warranty_expires)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(invoice)}
                        title={t('download_invoice')}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(invoice.invoice_id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              {t('upload_invoice')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">{t('select_file')} *</label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="invoice-file"
                />
                <label htmlFor="invoice-file" className="cursor-pointer">
                  {analyzing ? (
                    <div className="py-2">
                      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                      <p className="text-sm text-primary">{t('analyzing')}</p>
                    </div>
                  ) : formData.file ? (
                    <div>
                      <p className="text-sm text-green-600">{formData.file.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('info_auto_extracted')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">{t('allowed_formats')}</p>
                      <p className="text-xs text-muted-foreground">{t('max_file_size')}</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('product_name')} *</label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                placeholder="Ex: iPhone 15 Pro"
              />
            </div>

            {/* Store Name */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('store_name')} *</label>
              <Input
                value={formData.store_name}
                onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
                placeholder="Ex: Apple Store"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Purchase Date */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('purchase_date')} *</label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('amount')} *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Warranty */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('warranty_months')}</label>
              <Input
                type="number"
                value={formData.warranty_months}
                onChange={(e) => setFormData({ ...formData, warranty_months: e.target.value })}
                placeholder="24"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('category')}</label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Ex: Électronique"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('notes')}</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('optional_notes_placeholder')}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? t('loading') : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      <BottomNav />
    </div>
  );
}
